// Analytics/services/endpoints/inpatients.js
import axios from 'axios';
import { datalogger } from '../../../../../utils/index.jsx';
import { shouldServeMock } from '../demoMock';

const ANALYTICS_BASE_URL = import.meta.env.VITE_APP_API_URL + "/analytics";

// ============================================
// MOCK CONFIGURATION
// ============================================
const MOCK_CONFIG = {
    enabled: import.meta.env.VITE_MOCK_INPATIENTS === 'true',
    endpoints: {
        'total-admissions': true,
        'total-discharges': true,
        'discharge-rate': true,
        'average-stay-length': true,
        'bed-occupancy': true,
        'readmission-rate': true,
        'mortality-rate': true,
        'pharmacy-usage': true,
        'admissions-by-department': true,
        'indicators-by-department': true,
        'diagnosis-distribution': true,
        'discharge-diagnoses': true,
        'current-situation': true,
        'period-activity': true,
    },
    delay: 300
};

// ============================================
// MOCK DATA GENERATORS
// ============================================

const getMockTotalAdmissionsResponse = () => ({
    status: 200,
    chartData: {
        series: [
            { name: "Admissions", data: [45, 52, 38, 61, 55, 48, 67] }
        ],
        categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"],
        count: 366
    }
});

const getMockTotalDischargesResponse = () => ({
    status: 200,
    stats: {
        "dept-uuid-1": {
            name: "Cardiology",
            totalDischarges: 42,
            wards: {
                "ward-uuid-1": { name: "Ward A", discharges: 20 },
                "ward-uuid-2": { name: "Ward B", discharges: 22 }
            }
        },
        "dept-uuid-2": {
            name: "Neurology",
            totalDischarges: 30,
            wards: {
                "ward-uuid-3": { name: "Ward C", discharges: 15 },
                "ward-uuid-4": { name: "Ward D", discharges: 15 }
            }
        },
        "dept-uuid-3": {
            name: "Orthopedics",
            totalDischarges: 25,
            wards: {
                "ward-uuid-5": { name: "Ward E", discharges: 12 },
                "ward-uuid-6": { name: "Ward F", discharges: 13 }
            }
        },
        "dept-uuid-4": {
            name: "Pediatrics",
            totalDischarges: 20,
            wards: {
                "ward-uuid-7": { name: "Ward G", discharges: 10 },
                "ward-uuid-8": { name: "Ward H", discharges: 10 }
            }
        }
    }
});

const getMockDischargeRateResponse = () => ({
    status: 200,
    chartData: {
        series: [
            { name: "Discharge Rate", data: [93.3, 94.2, 92.1, 95.0, 93.5, 94.8, 96.2] }
        ],
        categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"],
        count: 94.3
    }
});

const getMockAvgStayLengthResponse = () => ({
    status: 200,
    chartData: {
        series: [
            { name: "Average Stay (Days)", data: [5.2, 5.8, 4.9, 6.1, 5.5, 5.3, 6.0] }
        ],
        categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"],
        count: 5.5
    }
});

const getMockBedOccupancyResponse = () => ({
    status: 200,
    chartData: {
        series: [
            { name: "Occupied", data: [85] },
            { name: "Available", data: [15] }
        ],
        categories: ["Bed Occupancy"],
        count: 85
    }
});

const getMockReadmissionRateResponse = () => ({
    status: 200,
    chartData: {
        series: [
            { name: "Readmission Rate", data: [8.5, 9.2, 7.8, 10.1, 8.9, 9.5, 10.3] }
        ],
        categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"],
        count: 9.2
    }
});

const getMockMortalityRateResponse = () => ({
    status: 200,
    chartData: {
        series: [
            { name: "Mortality Rate (%)", data: [2.1, 1.8, 0.9, 0.5] }
        ],
        categories: ["Cardiology", "Neurology", "Orthopedics", "Pediatrics"],
        count: 1.3
    }
});

const getMockPharmacyUsageResponse = () => ({
    status: 200,
    chartData: {
        series: [
            { name: "Pharmacy Usage", data: [120, 135, 128, 142, 138, 145, 150] }
        ],
        categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"],
        count: 137
    }
});

const getMockAdmissionsByDepartmentResponse = () => ({
    status: 200,
    chartData: {
        series: [
            { name: "Admissions", data: [45, 30, 25, 20] }
        ],
        categories: ["Cardiology", "Neurology", "Orthopedics", "Pediatrics"],
        count: 120
    }
});

const getMockIndicatorsByDepartmentResponse = () => ({
    status: 200,
    chartData: {
        series: [
            { name: "Indicators", data: [45, 30, 25, 20] }
        ],
        categories: ["Cardiology", "Neurology", "Orthopedics", "Pediatrics"],
        count: 120
    }
});

const getMockDiagnosisDistributionResponse = () => ({
    status: 200,
    chartData: {
        series: [
            { name: "Diagnosis", data: [28, 45, 32, 18, 15] }
        ],
        categories: [
            "Chronic obstructive pulmonary disease",
            "Essential hypertension",
            "Type 2 diabetes mellitus",
            "Acute myocardial infarction",
            "Pneumonia"
        ],
        count: 138
    }
});

const getMockCurrentSituationResponse = () => ({
    status: 200,
    stats: {
        totals: {
            currentPatients: 42,
            occupiedBeds: 38,
            totalCapacity: 50,
            occupancyRate: 76,
            averageCurrentLosDays: 5.2,
            patientsOver30Days: 4,
        },
        byDepartment: [
            {
                departmentUuid: 'dept-uuid-1',
                departmentName: 'Service de Médecine',
                currentPatients: 24,
                occupiedBeds: 22,
                availableBeds: 3,
                totalCapacity: 25,
                occupancyRate: 88,
                averageCurrentLosDays: 6.1,
                patientsOver7Days: 8,
                patientsOver14Days: 4,
                patientsOver30Days: 2,
            },
            {
                departmentUuid: 'dept-uuid-2',
                departmentName: 'Service de Chirurgie',
                currentPatients: 18,
                occupiedBeds: 16,
                availableBeds: 9,
                totalCapacity: 25,
                occupancyRate: 64,
                averageCurrentLosDays: 3.8,
                patientsOver7Days: 3,
                patientsOver14Days: 1,
                patientsOver30Days: 2,
            },
        ],
    },
});

const getMockPeriodActivityResponse = () => ({
    status: 200,
    stats: {
        totals: {
            admissions: 120,
            discharges: 105,
            deaths: 3,
            medianLosDays: 4.5,
            mortalityRate: 2.9,
            // Fewer than the 120 admissions: a demo where the two coincided would hide the
            // distinction the tile exists to show.
            uniquePatients: 96,
        },
        // SHER-897. Without this block a demo investigation renders the demographics card as an
        // error — `demographicsState` reads a missing population as "no answer", which is what
        // it is. SHER-896 added the equivalent to the outpatient mock for the same reason.
        patients: {
            ids: Array.from({ length: 96 }, (_, index) => index + 1),
            firstAdmissionDates: Array.from({ length: 96 }, (_, index) => index + 1).reduce(
                (dates, id) => ({
                    ...dates,
                    [id]: `${new Date().getFullYear()}-${String((id % 12) + 1).padStart(2, '0')}-${String((id % 27) + 1).padStart(2, '0')}`,
                }),
                {},
            ),
        },
        byDepartment: [
            {
                departmentUuid: 'dept-uuid-1',
                departmentName: 'Service de Médecine',
                admissions: 70,
                discharges: 60,
                deaths: 2,
                medianLosDays: 5,
                mortalityRate: 3.3,
                averageOccupancyRate: 78,
            },
            {
                departmentUuid: 'dept-uuid-2',
                departmentName: 'Service de Chirurgie',
                admissions: 50,
                discharges: 45,
                deaths: 1,
                medianLosDays: 3,
                mortalityRate: 2.2,
                averageOccupancyRate: 65,
            },
        ],
        chart: {
            bucket: 'week',
            categories: ['2026-08-01', '2026-08-08'],
            series: [
                { name: 'admissions', data: [55, 65] },
                { name: 'discharges', data: [48, 57] },
                { name: 'deaths', data: [1, 2] },
            ],
        },
    },
});

// Mock registry
/**
 * SHER-583. Fewer discharges per diagnosis than the diagnosis donut's counts, on purpose: the two
 * panels answer different questions (any ICD record in the window vs the discharge form only), and
 * a demo where they matched would teach the wrong thing.
 */
const getMockDischargeDiagnosesResponse = () => ({
    status: 200,
    stats: {
        diagnoses: [
            // SHER-583: patientIds/patientsLength feed the Female/Male/Total columns. Fewer
            // patients than discharges on purpose — readmissions are why the table carries both.
            { code: 'BA00', name: 'Acute rheumatic fever', discharges: 18, departments: ['Service de Médecine', 'Service de Pédiatrie'], patientIds: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15], patientsLength: 15 },
            { code: 'CA40', name: 'Pneumonia', discharges: 14, departments: ['Service de Médecine'], patientIds: [16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27], patientsLength: 12 },
            { code: '1F40', name: 'Malaria', discharges: 11, departments: ['Service de Médecine', 'Service de Chirurgie', 'Service de Pédiatrie'], patientIds: [28, 29, 30, 31, 32, 33, 34, 35, 36, 37], patientsLength: 10 },
            { code: 'DA60', name: 'Peptic ulcer', discharges: 6, departments: ['Service de Chirurgie'], patientIds: [38, 39, 40, 41, 42], patientsLength: 5 },
        ],
        totalDischarges: 49,
    },
});

const MOCK_REGISTRY = {
    'total-admissions': getMockTotalAdmissionsResponse,
    'total-discharges': getMockTotalDischargesResponse,
    'discharge-rate': getMockDischargeRateResponse,
    'average-stay-length': getMockAvgStayLengthResponse,
    'bed-occupancy': getMockBedOccupancyResponse,
    'readmission-rate': getMockReadmissionRateResponse,
    'mortality-rate': getMockMortalityRateResponse,
    'pharmacy-usage': getMockPharmacyUsageResponse,
    'admissions-by-department': getMockAdmissionsByDepartmentResponse,
    'indicators-by-department': getMockIndicatorsByDepartmentResponse,
    'diagnosis-distribution': getMockDiagnosisDistributionResponse,
    'discharge-diagnoses': getMockDischargeDiagnosesResponse,
    'current-situation': getMockCurrentSituationResponse,
    'period-activity': getMockPeriodActivityResponse,
};

const getAuthHeaders = () => ({
    headers: { "Authorization": localStorage.getItem("jwt") }
});

const buildOutpatientsUrl = (endpoint, { uuidInvestigation, uuidDepartment, startDate, endDate }) => {
    const baseUrl = `${ANALYTICS_BASE_URL}/${uuidInvestigation}/inpatients`;
    const endpointPath = endpoint || '';
    if (endpointPath === 'current-situation') {
        return `${baseUrl}/${endpointPath}/${uuidDepartment}`;
    }
    console.log('Building URL: inpatients', endpointPath, uuidDepartment, startDate, endDate);
    return `${baseUrl}/${endpointPath}/${uuidDepartment}/startDate/${startDate}/endDate/${endDate}`;
};

const makeOutpatientsRequest = async (params, signal) => {
    const { endpointPath } = params;
    const url = buildOutpatientsUrl(endpointPath, params);
    console.log('Making request to:', params, endpointPath);
    
    // Return mock data if enabled
    if (shouldServeMock(endpointPath, MOCK_CONFIG, MOCK_REGISTRY)) {
        console.log(`[MOCK] Returning mock data for: ${endpointPath}`);
        await new Promise(resolve => setTimeout(resolve, MOCK_CONFIG.delay));
        return MOCK_REGISTRY[endpointPath]();
    }
    
    try {
        const response = await axios.get(url, {
            ...getAuthHeaders(),
            signal
        });
        if (response.status === 200) {
            console.log('Inpatients API Response:', endpointPath, response.data);
            return response.data;
        }
    } catch (error) {
        console.error('Error in inp:', endpointPath, error);
    }
};

const createEndpoint = (endpointPath) => {
    console.log('Creating inpatients endpoint:', endpointPath);
    return datalogger((uuidInvestigation, uuidDepartment, startDate, endDate, signal) => 
        makeOutpatientsRequest({ 
            endpointPath,
            uuidInvestigation, 
            uuidDepartment, 
            startDate, 
            endDate 
        }, signal)
    );
};

const createLiveEndpoint = (endpointPath) => {
    return datalogger((uuidInvestigation, uuidDepartment, signal) =>
        makeOutpatientsRequest({
            endpointPath,
            uuidInvestigation,
            uuidDepartment,
        }, signal)
    );
};

export const inpatientsServices = {
    getTotalAdmissions: createEndpoint('total-admissions'),
    getAgeGroup: createEndpoint('total-admissions'),
    getTotalDischarges: createEndpoint('total-discharges'),
    getDischargeRate: createEndpoint('discharge-rate'),
    getAvgStayLength: createEndpoint('average-stay-length'),
    getBedOccupancy: createEndpoint('bed-occupancy'),
    getReadmissionRate: createEndpoint('readmission-rate'),
    getMortalityRate: createEndpoint('mortality-rate'),
    getPharmacyUsage: createEndpoint('pharmacy-usage'),
    getAdmissionsByDepartment: createEndpoint('admissions-by-department'),
    getIndicatorsByDepartment: createEndpoint('indicators-by-department'),
    getDiagnosis: createEndpoint('diagnosis-distribution'),
    getDischargeDiagnoses: createEndpoint('discharge-diagnoses'),
    getCurrentSituation: createLiveEndpoint('current-situation'),
    getPeriodActivity: createEndpoint('period-activity'),
};
